// FILE: src/services/reportService.js
//
// Pure helpers for the question-report feature plus Supabase persistence.
// No module-level state —
// all report objects are created here and stored in App.js React state, so
// React re-renders work normally and no module reset is needed in tests.
//
// ── Data model ────────────────────────────────────────────────────────────────
//
//   {
//     reportId        string   — unique id ("rpt-<timestamp>-<random>")
//     questionId      string   — stable question id (e.g. "pmp-0001")
//     referenceId     string   — human-readable ref (e.g. "PMP-0001")
//     examId          string   — "pmp" | "cpmai"
//     category        string   — one of REPORT_CATEGORIES
//     comments        string   — learner's description of the issue (trimmed, required)
//     notes           string   — SME resolution notes added during triage (trimmed)
//     questionSnapshot string  — question text at time of report
//     selectedAnswers string[] — choice ids the learner had selected (may be [])
//     reporter        string   — optional reporter name (trimmed)
//     createdAt       number   — Date.now() at creation time
//     status          string   — starts "new"; transitions via REPORT_STATUSES
//   }
//
// ── Status lifecycle ──────────────────────────────────────────────────────────
//
//   new → fixed
//     ↘ won't fix
//
//   While status is "new" the question is quarantined — excluded from new
//   sessions.  Setting status to "fixed" or "won't fix" (with optional SME
//   notes) releases the question back into the active pool.
//
//   Transitions are managed by App.js (handleUpdateReport) which maps over the
//   reports array and returns a new array — no mutation.

import { supabase } from './supabaseClient';

// ── Constants ─────────────────────────────────────────────────────────────────

export const REPORT_CATEGORIES = [
  'Incorrect answer',
  'Explanation outdated',
  'OCR / typo issue',
  'Ambiguous wording',
  'PMBOK alignment issue',
  'Formatting / display issue',
  'Duplicate / overlap',
  'Other',
];

export const REPORT_STATUSES = ['new', 'fixed', "won't fix"];

// ── createReport ──────────────────────────────────────────────────────────────

/**
 * Creates a new immutable report object.  Pure function — no side effects.
 *
 * @param {object} params
 * @param {string}   params.questionId       — required; stable question id
 * @param {string}   [params.referenceId]    — human-readable ref ("PMP-0001")
 * @param {string}   params.examId           — required; "pmp" | "cpmai"
 * @param {string}   params.category         — required; must be in REPORT_CATEGORIES
 * @param {string}   params.comments         — required; learner's description (non-blank)
 * @param {string}   [params.questionSnapshot] — question text at time of report
 * @param {string[]} [params.selectedAnswers]  — learner's selected choice ids
 * @param {string}   [params.reporter]        — optional reporter name
 * @returns {object} immutable report object with status:"new" and notes:""
 * @throws {Error} if any required field is missing or blank
 */
export function createReport({
  questionId,
  referenceId = '',
  examId,
  category,
  comments,
  questionSnapshot = '',
  selectedAnswers = [],
  reporter = '',
}) {
  if (!questionId)                    throw new Error('questionId is required');
  if (!examId)                        throw new Error('examId is required');
  if (!category)                      throw new Error('category is required');
  if (!comments || !comments.trim())  throw new Error('comments is required');

  return {
    reportId:         `rpt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    questionId:       String(questionId),
    referenceId:      String(referenceId),
    examId:           String(examId),
    category,
    comments:         comments.trim(),
    notes:            '',
    questionSnapshot: String(questionSnapshot),
    selectedAnswers:  [...selectedAnswers],
    reporter:         reporter?.trim() ?? '',
    createdAt:        Date.now(),
    status:           'new',
  };
}

// ── Supabase persistence ──────────────────────────────────────────────────────

/**
 * Converts an in-memory report object to a Supabase row (snake_case columns).
 */
function toSupabaseRow(report) {
  return {
    id:                report.reportId,
    question_id:       report.questionId,
    reference_id:      report.referenceId,
    exam_id:           report.examId,
    category:          report.category,
    comments:          report.comments,
    notes:             report.notes || '',
    question_snapshot: report.questionSnapshot || '',
    selected_answers:  report.selectedAnswers || [],
    reporter:          report.reporter || '',
    status:            report.status,
    created_at:        new Date(report.createdAt).toISOString(),
  };
}

/**
 * Fire-and-forget: insert a newly created report into the Supabase reports table.
 * Errors are logged but never thrown — local React state is authoritative.
 *
 * @param {object} report - immutable report object from createReport()
 */
export async function persistReport(report) {
  try {
    const { error } = await supabase
      .from('reports')
      .insert(toSupabaseRow(report));
    if (error) {
      console.warn('[reportService] Supabase insert failed:', error.message);
    }
  } catch (err) {
    console.warn('[reportService] Unexpected Supabase error:', err);
  }
}

/**
 * Fire-and-forget: update an existing report's status and notes in Supabase.
 * Called when an SME triages a report (changes status to "fixed" or "won't fix").
 *
 * @param {string} reportId - the report's unique id (maps to `id` column)
 * @param {object} changes  - partial update: { status?, notes? }
 */
export async function updateReportInSupabase(reportId, changes) {
  try {
    const update = {};
    if (changes.status !== undefined) update.status = changes.status;
    if (changes.notes  !== undefined) update.notes  = changes.notes;

    const { error } = await supabase
      .from('reports')
      .update(update)
      .eq('id', reportId);
    if (error) {
      console.warn('[reportService] Supabase update failed:', error.message);
    }
  } catch (err) {
    console.warn('[reportService] Unexpected Supabase error:', err);
  }
}
